import { useMemo, useState } from "react"
import { motion } from "motion/react"
import { toast } from "sonner"
import { Gift01, Copy01, Share06, UserPlus01 } from "@untitledui/icons"
import { ProtectedRoute } from "@/lib/auth/protected-route"
import { AppLayout } from "@/components/layout/app-layout"
import { ALL_ROLES } from "@/lib/team/roles"
import { useAuth } from "@/lib/auth/auth-context"
import { useReferrals } from "@/lib/api/use-referrals"
import type { Referral, ReferralStage } from "@/lib/api/referrals"
import { cx } from "@/lib/utils/cx"

// Refer-a-friend — the advisor sends their link to a consumer and tracks
// the referral all the way from "showed up" to "completed", with the money
// attached. UI/UX surface (Anthony, Jul 29): the share action is a copied
// link and the pipeline renders real rows; the minting/stage engine and pay
// policy are a later build.

const STAGES: ReferralStage[] = ["invited", "showed_up", "estimate", "booked", "completed"]

const STAGE_META: Record<ReferralStage, { label: string; dot: string; chip: string }> = {
  invited: { label: "Invited", dot: "bg-zinc-300", chip: "bg-zinc-100 text-zinc-600" },
  showed_up: { label: "Showed up", dot: "bg-amber-500", chip: "bg-amber-50 text-amber-800" },
  estimate: { label: "Estimate", dot: "bg-teal-500", chip: "bg-teal-50 text-teal-800" },
  booked: { label: "Booked", dot: "bg-sky-500", chip: "bg-sky-50 text-sky-800" },
  completed: { label: "Completed", dot: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-800" },
}

function relTime(iso: string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000)
  if (mins < 60) return "today"
  const h = Math.round(mins / 60)
  if (h < 24) return "today"
  const d = Math.round(h / 24)
  if (d === 1) return "yesterday"
  if (d < 7) return `${d}d ago`
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

const RISE = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] as const } },
}
const STAGGER = { staggerChildren: 0.06 }

export default function ReferralsPage() {
  const { user } = useAuth()
  const userId = user?.id ? Number(user.id) : null
  const { data: referrals = [], isLoading } = useReferrals(userId)
  const [shared, setShared] = useState(false)

  // The advisor's personal link — deterministic from their identity. The
  // real minting service replaces this; the shape is the design statement.
  const refCode = useMemo(() => {
    const ini = `${user?.firstName?.[0] ?? "A"}${user?.lastName?.[0] ?? "D"}`.toUpperCase()
    return `${ini}${String(userId ?? 0).padStart(2, "0")}`
  }, [user, userId])
  const refLink = `https://etx.link/r/${refCode}`

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Get your car damage checked",
          text: "Get a free damage check and estimate — book straight from this link:",
          url: refLink,
        })
      } else {
        await navigator.clipboard.writeText(refLink)
        toast.success("Link copied — paste it in a text")
      }
      setShared(true)
    } catch {
      /* user closed the sheet — nothing to do */
    }
  }
  const copy = () =>
    navigator.clipboard
      .writeText(refLink)
      .then(() => toast.success("Link copied"))
      .catch(() => toast.error("Couldn't copy"))

  const counts = useMemo(() => {
    const showedPlus = referrals.filter((r) => r.stage !== "invited").length
    const completed = referrals.filter((r) => r.stage === "completed")
    const inFlight = referrals.filter(
      (r) => r.stage !== "completed" && r.stage !== "invited",
    )
    return {
      referred: referrals.length,
      showedPlus,
      completed: completed.length,
      earned: completed.reduce((s, r) => s + (r.rewardAmount ?? 0), 0),
      onTheWay: inFlight.reduce((s, r) => s + (r.rewardAmount ?? 0), 0),
    }
  }, [referrals])

  return (
    <ProtectedRoute allowedRoles={ALL_ROLES}>
      <AppLayout>
        <div className="relative min-h-screen bg-gradient-to-b from-zinc-50 to-white pb-24">
          <div aria-hidden className="pointer-events-none fixed inset-0">
            <div className="absolute -top-32 right-[-200px] size-[680px] rounded-full bg-emerald-200/20 blur-3xl" />
          </div>

          <motion.div
            initial="hidden"
            animate="show"
            variants={{ show: { transition: STAGGER } }}
            className="relative max-w-[560px] mx-auto px-4 md:px-6 pt-5 md:pt-8 space-y-4"
          >
            {/* Header */}
            <motion.div variants={RISE}>
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-600">
                Refer a friend
              </p>
              <h1 className="text-[30px] leading-[1.08] font-black text-zinc-900 tracking-tight mt-0.5 max-w-[420px]">
                Send a friend,
                <br />
                get paid at the finish
              </h1>
            </motion.div>

            {/* The share card — the one action this page exists for. */}
            <motion.div variants={RISE} className="relative">
              <div
                aria-hidden
                className="absolute -inset-1 rounded-[28px] bg-gradient-to-br from-emerald-300/30 via-emerald-200/15 to-transparent blur-2xl"
              />
              <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-800 shadow-2xl shadow-emerald-700/30 p-5">
                <div className="flex items-center gap-3.5">
                  <div className="size-12 rounded-2xl bg-white/15 backdrop-blur grid place-items-center shrink-0">
                    <Gift01 className="size-6 text-white" aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-emerald-100/80">
                      Your link
                    </p>
                    <p className="text-[19px] font-black text-white tracking-tight truncate">
                      etx.link/r/{refCode}
                    </p>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2.5">
                  <button
                    type="button"
                    onClick={share}
                    className="h-[52px] rounded-2xl bg-white text-emerald-800 text-[15px] font-black inline-flex items-center justify-center gap-2 shadow-lg shadow-emerald-900/20 active:scale-[0.98] transition-transform"
                  >
                    <Share06 className="size-[18px]" aria-hidden />
                    {shared ? "Share again" : "Share it"}
                  </button>
                  <button
                    type="button"
                    onClick={copy}
                    className="h-[52px] rounded-2xl bg-white/15 ring-1 ring-inset ring-white/25 text-white text-[15px] font-black inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                  >
                    <Copy01 className="size-[18px]" aria-hidden />
                    Copy link
                  </button>
                </div>
                <p className="mt-3 text-[11.5px] text-emerald-50/80 leading-snug">
                  They book a free damage check from your link — you track it here
                  from the moment they show up to the finished repair.
                </p>
              </div>
            </motion.div>

            {/* The receipts */}
            <motion.div
              variants={RISE}
              className="rounded-2xl border border-zinc-200/70 bg-white shadow-sm px-5 py-4 grid grid-cols-4 gap-2"
            >
              <RefStat value={String(counts.referred)} label="Referred" />
              <RefStat value={String(counts.showedPlus)} label="Showed up" />
              <RefStat value={String(counts.completed)} label="Completed" />
              <RefStat
                value={`$${counts.earned.toLocaleString("en-US")}`}
                label="Earned"
                accent
                sub={counts.onTheWay > 0 ? `+$${counts.onTheWay} on the way` : undefined}
              />
            </motion.div>

            {/* The pipeline — every referral, tracked stage by stage. */}
            <motion.div
              variants={RISE}
              className="rounded-2xl border border-zinc-200/70 bg-white shadow-sm overflow-hidden"
            >
              <div className="px-5 pt-4 pb-2 flex items-center justify-between">
                <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-zinc-400">
                  Your referrals
                </p>
                {referrals.length > 0 && (
                  <span className="text-[11px] font-semibold text-zinc-400 tabular-nums">
                    {referrals.length}
                  </span>
                )}
              </div>

              {isLoading ? (
                <div className="px-5 pb-5 space-y-2 animate-pulse">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="h-16 rounded-xl bg-zinc-50" />
                  ))}
                </div>
              ) : referrals.length === 0 ? (
                <div className="px-5 pb-6 pt-2 text-center">
                  <div className="size-11 rounded-2xl bg-zinc-100 grid place-items-center mx-auto mb-3">
                    <UserPlus01 className="size-5 text-zinc-400" aria-hidden />
                  </div>
                  <p className="text-[13.5px] font-semibold text-zinc-900">
                    Nobody referred yet
                  </p>
                  <p className="text-[12.5px] text-zinc-500 mt-1 max-w-[260px] mx-auto leading-snug">
                    Share your link with a customer whose friend just got rear-ended —
                    that's the whole move.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-zinc-50 pb-1">
                  {referrals.map((r) => (
                    <ReferralRow key={r.id} r={r} />
                  ))}
                </div>
              )}
            </motion.div>

            {/* How it works — three steps, no manual. */}
            <motion.div
              variants={RISE}
              className="rounded-2xl border border-zinc-200/70 bg-white shadow-sm px-5 py-4"
            >
              <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-zinc-400 mb-2.5">
                How it works
              </p>
              <div className="space-y-2">
                <Step n={1} text="Send your link to anyone with a banged-up car." />
                <Step n={2} text="They book a free check and show up — you'll see every step here." />
                <Step n={3} text="The repair completes, the reward lands in your wallet." />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </AppLayout>
    </ProtectedRoute>
  )
}

function RefStat({
  value,
  label,
  sub,
  accent,
}: {
  value: string
  label: string
  sub?: string
  accent?: boolean
}) {
  return (
    <div className="min-w-0">
      <p
        className={cx(
          "text-[21px] font-black tabular-nums leading-none truncate",
          accent ? "text-emerald-700" : "text-zinc-900",
        )}
      >
        {value}
      </p>
      <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-zinc-400 mt-1">
        {label}
      </p>
      {sub && <p className="text-[9.5px] text-emerald-600 font-semibold mt-0.5 truncate">{sub}</p>}
    </div>
  )
}

function ReferralRow({ r }: { r: Referral }) {
  const idx = STAGES.indexOf(r.stage)
  const meta = STAGE_META[r.stage]
  const done = r.stage === "completed"
  return (
    <div className="px-5 py-3">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <p className="text-[13.5px] font-bold text-zinc-900 truncate">{r.name}</p>
            <span
              className={cx(
                "shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-bold",
                meta.chip,
              )}
            >
              {meta.label}
            </span>
          </div>
          <p className="text-[11.5px] text-zinc-400 truncate mt-0.5">
            {r.vehicleLabel ?? "Vehicle TBD"} · {relTime(r.stageAt)}
          </p>
        </div>
        <div className="shrink-0 text-right">
          {done ? (
            <p className="text-[14px] font-black text-emerald-700 tabular-nums">
              +${r.rewardAmount ?? 0}
            </p>
          ) : r.rewardAmount ? (
            <p className="text-[11.5px] font-semibold text-zinc-400 tabular-nums">
              ${r.rewardAmount} <span className="font-medium">at finish</span>
            </p>
          ) : null}
        </div>
      </div>
      {/* The journey — five stops, filled to where they stand. */}
      <div className="mt-2 flex items-center gap-1">
        {STAGES.map((s, i) => (
          <div
            key={s}
            className={cx(
              "h-1.5 flex-1 rounded-full transition-colors",
              i <= idx ? STAGE_META[STAGES[Math.min(idx, STAGES.length - 1)]].dot : "bg-zinc-100",
              i <= idx && !done && i === idx && "animate-pulse",
            )}
            title={STAGE_META[s].label}
          />
        ))}
      </div>
    </div>
  )
}

function Step({ n, text }: { n: number; text: string }) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="size-5 rounded-full bg-emerald-100 text-emerald-800 grid place-items-center text-[10.5px] font-black shrink-0 mt-0.5">
        {n}
      </span>
      <p className="text-[12.5px] text-zinc-600 leading-snug">{text}</p>
    </div>
  )
}
